/**
 * KeywordRegistry — the keywords that recognise an imported document's
 * SENDER and TYPE before it is read (`import_rules.py`).
 *
 * Two kinds of rule:
 *
 * - a SENDER rule — the keywords that say who sent it: their name, email or
 *   email domain, GSTIN / PAN / VAT, phone, city, PIN. Several of DIFFERENT
 *   kinds must match (a name alone never recognises anyone);
 * - a DOCUMENT TYPE rule — the heading and labels that say what it is
 *   ("Purchase Order", "Return Claim"), for any sender, or one sender's own
 *   wording.
 *
 * Keywords the AI proposed are DRAFTS until a saved import uses them
 * unchanged or a manager confirms them here. A person's rule is never
 * rewritten by the AI (its later proposals are shown as suggestions). Every
 * save is checked against the stored sample documents: a keyword printed on
 * another sender's documents is not kept (it singles nobody out), and a rule
 * that would also recognise another sender's or type's documents is reported.
 *
 * `pattern` keywords use placeholders — {num} {word} {code} {date} {sep}
 * {gap:N} and (a|b) — never a raw regular expression.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, FileText, Loader2, Plus, Trash2, UserRound, X } from 'lucide-react'
import {
  importErrorInfo, type ImportKeywordPattern, type ImportKeywordRule, type ImportRuleSaveResult,
  type ImportRulesList, type RecordImportTarget, type RecordImportTransport,
} from './types'

const KINDS: Array<{ value: string; label: string; hint: string }> = [
  { value: 'name', label: 'Name', hint: 'Acme Traders (Pvt Ltd / Limited are ignored)' },
  { value: 'email', label: 'Email', hint: 'orders@acme.com, or @acme.com for anyone there' },
  { value: 'tax_id', label: 'Tax ID', hint: 'GSTIN, PAN or VAT number' },
  { value: 'phone', label: 'Phone', hint: 'Compared on its last digits — any format' },
  { value: 'city', label: 'City', hint: 'Mumbai' },
  { value: 'postal', label: 'PIN / ZIP', hint: '400001' },
  { value: 'phrase', label: 'Phrase', hint: 'purchase order|indent — whole words; | for alternatives' },
  { value: 'pattern', label: 'Pattern', hint: 'PO {sep} No {sep} {num} — placeholders, never a regex' },
]
const kindLabel = (k: string) => KINDS.find((x) => x.value === k)?.label || k

interface Editing {
  isNew: boolean
  scope: 'sender' | 'doc_type'
  sender: string
  doc_type: string
  label: string
  patterns: ImportKeywordPattern[]
  suggested?: ImportKeywordPattern[]
}

export function KeywordRegistry({ target, transport, senderLabel }: {
  target: RecordImportTarget
  transport: RecordImportTransport
  senderLabel: string
}) {
  const describe = transport.describeError ?? importErrorInfo
  const [data, setData] = useState<ImportRulesList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [result, setResult] = useState<ImportRuleSaveResult | null>(null)

  const load = useCallback(async () => {
    if (!transport.listRules) return
    try {
      setData(await transport.listRules(target))
      setError(null)
    } catch (e) {
      console.warn('[KeywordRegistry] could not load the keywords', e)
      setError(describe(e, 'The keywords could not be loaded.').message)
    }
  }, [transport, target, describe])

  useEffect(() => { void load() }, [load])

  const senders = useMemo(() => (data?.rules || []).filter((r) => r.scope === 'sender'), [data])
  const types = useMemo(() => (data?.rules || []).filter((r) => r.scope === 'doc_type'), [data])
  const typeLabel = useCallback((code: string) =>
    data?.doc_types.find((d) => d.code === code)?.label || code.replace(/_/g, ' '), [data])

  const open = (r: ImportKeywordRule) => {
    setResult(null)
    setEditing({
      isNew: false, scope: r.scope, sender: r.sender_code, doc_type: r.doc_type,
      label: r.label || '', patterns: r.patterns.map((p) => ({ ...p })),
      suggested: r.suggested && r.suggested.length ? r.suggested : undefined,
    })
  }

  const save = async () => {
    if (!editing || !transport.saveRule) return
    setBusy(true)
    setError(null)
    try {
      const res = await transport.saveRule(target, {
        scope: editing.scope, sender: editing.scope === 'sender' ? editing.sender.trim() : (editing.sender || '*'),
        doc_type: editing.scope === 'doc_type' ? editing.doc_type.trim() : undefined,
        label: editing.label.trim(), patterns: editing.patterns.filter((p) => p.value.trim()),
      })
      setResult(res)
      setEditing((e) => e && { ...e, isNew: false, patterns: res.rule.patterns.map((p) => ({ ...p })),
        sender: res.rule.sender_code, doc_type: res.rule.doc_type })
      await load()
    } catch (e) {
      console.warn('[KeywordRegistry] could not save the rule', e)
      setError(describe(e, 'The keywords could not be saved.').message)
    } finally {
      setBusy(false)
    }
  }

  const confirm = async (r: ImportKeywordRule) => {
    if (!transport.setRuleStatus) return
    setBusy(true)
    try {
      await transport.setRuleStatus(target, { scope: r.scope, sender_code: r.sender_code,
        doc_type: r.doc_type, status: 'active' })
      await load()
    } catch (e) {
      console.warn('[KeywordRegistry] could not confirm the rule', e)
      setError(describe(e, 'The rule could not be confirmed.').message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (r: { scope: string; sender_code: string; doc_type: string }) => {
    if (!transport.deleteRule) return
    setBusy(true)
    try {
      await transport.deleteRule(target, { scope: r.scope, sender_code: r.sender_code, doc_type: r.doc_type })
      setEditing(null)
      setResult(null)
      await load()
    } catch (e) {
      console.warn('[KeywordRegistry] could not delete the rule', e)
      setError(describe(e, 'The rule could not be removed.').message)
    } finally {
      setBusy(false)
    }
  }

  const setPattern = (i: number, patch: Partial<ImportKeywordPattern>) =>
    setEditing((e) => e && { ...e, patterns: e.patterns.map((p, j) => (j === i ? { ...p, ...patch } : p)) })

  if (!transport.listRules) return null

  if (editing) {
    const isSender = editing.scope === 'sender'
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" style={btn} onClick={() => { setEditing(null); setResult(null) }}>
            <X size={14} /> All keywords
          </button>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            {editing.isNew ? (isSender ? `New ${senderLabel.toLowerCase()}` : 'New document type')
              : isSender ? (editing.label || editing.sender)
                : `${typeLabel(editing.doc_type)}${editing.sender && editing.sender !== '*' ? ` · ${editing.sender}` : ''}`}
          </h3>
        </div>
        {error && <div role="alert" style={errorBox}><AlertCircle size={15} /> {error}</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {isSender ? (
            <label style={field}>{senderLabel}
              <input value={editing.sender} disabled={!editing.isNew} style={input} placeholder="Code or name"
                onChange={(e) => setEditing({ ...editing, sender: e.target.value })} />
            </label>
          ) : (
            <>
              <label style={field}>Type label
                <input value={editing.label} style={input} placeholder="Return claim"
                  onChange={(e) => setEditing({ ...editing, label: e.target.value,
                    ...(editing.isNew ? { doc_type: codeOf(e.target.value) } : {}) })} />
              </label>
              <label style={field}>Code
                <input value={editing.doc_type} disabled={!editing.isNew} style={input} placeholder="return_claim"
                  onChange={(e) => setEditing({ ...editing, doc_type: e.target.value })} />
              </label>
              <label style={field}>For
                <input value={editing.sender === '*' ? '' : editing.sender} disabled={!editing.isNew} style={input}
                  placeholder={`Any ${senderLabel.toLowerCase()} (or one, by code)`}
                  onChange={(e) => setEditing({ ...editing, sender: e.target.value || '*' })} />
              </label>
            </>
          )}
        </div>
        <p style={note}>
          {isSender
            ? 'Keywords printed on their documents. At least two DIFFERENT kinds must match — a name with a GSTIN, an email with a phone.'
            : 'The heading and labels that make this document what it is. A heading counts double; mark a keyword “Headings only” when it must be the heading.'}
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>{['Kind', 'Keyword', 'Where', 'Must', 'Excludes', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {editing.patterns.map((p, i) => (
              <tr key={i}>
                <td style={{ ...td, width: 130 }}>
                  <select value={p.kind} style={input} aria-label="Kind"
                    onChange={(e) => setPattern(i, { kind: e.target.value })}>
                    {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <input value={p.value} style={input} aria-label="Keyword"
                    placeholder={KINDS.find((k) => k.value === p.kind)?.hint}
                    onChange={(e) => setPattern(i, { value: e.target.value })} />
                </td>
                <td style={{ ...td, width: 150 }}>
                  <select value={p.zone || 'any'} style={input} aria-label="Where"
                    onChange={(e) => setPattern(i, { zone: e.target.value })}>
                    <option value="any">Anywhere</option>
                    <option value="title">Headings only</option>
                    <option value="letterhead">Letterhead (first lines)</option>
                  </select>
                </td>
                <td style={{ ...td, width: 60, textAlign: 'center' }}>
                  <input type="checkbox" checked={!!p.required} aria-label="Must match"
                    onChange={(e) => setPattern(i, { required: e.target.checked, exclude: false })} />
                </td>
                <td style={{ ...td, width: 70, textAlign: 'center' }}>
                  <input type="checkbox" checked={!!p.exclude} aria-label="Excludes"
                    title="A document carrying this is never this one"
                    onChange={(e) => setPattern(i, { exclude: e.target.checked, required: false })} />
                </td>
                <td style={{ ...td, width: 40 }}>
                  <button type="button" style={iconBtn} aria-label="Remove keyword"
                    onClick={() => setEditing({ ...editing, patterns: editing.patterns.filter((_, j) => j !== i) })}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <button type="button" style={btn} onClick={() => setEditing({ ...editing,
            patterns: [...editing.patterns, { kind: isSender ? 'name' : 'phrase', value: '', zone: 'any' }] })}>
            <Plus size={14} /> Add a keyword
          </button>
        </div>
        {editing.suggested && (
          <div style={suggestBox}>
            <div style={{ fontWeight: 600 }}>The AI also proposes</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0' }}>
              {editing.suggested.map((p, i) => <span key={i} style={chip}>{kindLabel(p.kind)}: {p.value}</span>)}
            </div>
            <button type="button" style={btn} onClick={() => setEditing({ ...editing,
              patterns: [...editing.patterns, ...(editing.suggested || [])], suggested: undefined })}>
              Add them
            </button>
          </div>
        )}
        {result && <SaveReport result={result} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div>
            {!editing.isNew && transport.deleteRule && (
              <button type="button" style={btn} disabled={busy}
                onClick={() => void remove({ scope: editing.scope, sender_code: editing.sender || '*',
                  doc_type: editing.scope === 'sender' ? '*' : editing.doc_type })}>
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
          <button type="button" style={primaryBtn} disabled={busy || !transport.saveRule} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save keywords'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={note}>
        How a document is recognised BEFORE it is read: its {senderLabel.toLowerCase()} by keywords of several kinds,
        its type by its heading and labels. Keywords the AI proposed are drafts until an import using them is saved
        unchanged, or you confirm them.
      </p>
      {error && <div role="alert" style={errorBox}><AlertCircle size={15} /> {error}</div>}
      {data === null && !error && <div style={note}><Loader2 size={14} /> Loading…</div>}
      {data && (
        <>
          <RuleTable title={`${senderLabel}s`} icon={<UserRound size={14} />} rules={senders} busy={busy}
            name={(r) => r.label || r.sender_code} onOpen={open} onConfirm={(r) => void confirm(r)}
            onAdd={transport.saveRule ? () => { setResult(null); setEditing({ isNew: true, scope: 'sender',
              sender: '', doc_type: '*', label: '', patterns: [{ kind: 'name', value: '', zone: 'any' }] }) } : undefined} />
          <RuleTable title="Document types" icon={<FileText size={14} />} rules={types} busy={busy}
            name={(r) => `${typeLabel(r.doc_type)}${r.sender_code !== '*' ? ` · ${r.sender_code}’s wording` : ''}`}
            onOpen={open} onConfirm={(r) => void confirm(r)}
            onAdd={transport.saveRule ? () => { setResult(null); setEditing({ isNew: true, scope: 'doc_type',
              sender: '*', doc_type: '', label: '', patterns: [{ kind: 'phrase', value: '', zone: 'title' }] }) } : undefined} />
          {(data.stop_list || []).length > 0 && (
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Printed on several senders’ documents</div>
              <p style={note}>These identify nobody (usually your own company’s details), so they are never a sender’s keyword.</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {(data.stop_list || []).map((s) => (
                  <span key={`${s.kind}:${s.value}`} style={chip} title={s.senders.join(', ')}>
                    {kindLabel(s.kind)}: {s.value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RuleTable({ title, icon, rules, name, busy, onOpen, onConfirm, onAdd }: {
  title: string
  icon: React.ReactNode
  rules: ImportKeywordRule[]
  name: (r: ImportKeywordRule) => string
  busy: boolean
  onOpen: (r: ImportKeywordRule) => void
  onConfirm: (r: ImportKeywordRule) => void
  onAdd?: () => void
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 }}>
          {icon} {title}
        </span>
        <div style={{ flex: 1 }} />
        {onAdd && <button type="button" style={btn} onClick={onAdd}><Plus size={14} /> Add</button>}
      </div>
      {rules.length === 0 ? <div style={note}>None yet — they are proposed as documents are imported.</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {rules.map((r) => (
              <tr key={`${r.scope}|${r.sender_code}|${r.doc_type}`} style={{ cursor: 'pointer' }} onClick={() => onOpen(r)}>
                <td style={{ ...td, width: '28%' }}>
                  {name(r)}
                  {r.scope === 'sender' && r.label && <div style={sub}>{r.sender_code}</div>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.patterns.slice(0, 6).map((p, i) => (
                      <span key={i} style={{ ...chip, textDecoration: p.exclude ? 'line-through' : undefined }}>
                        {kindLabel(p.kind)}: {p.value}
                      </span>
                    ))}
                    {r.patterns.length > 6 && <span style={sub}>+{r.patterns.length - 6}</span>}
                  </div>
                </td>
                <td style={{ ...td, width: 170 }} onClick={(e) => e.stopPropagation()}>
                  {r.status === 'draft' ? (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <span style={badge(true)}>AI draft</span>
                      <button type="button" style={smallBtn} disabled={busy} onClick={() => onConfirm(r)}>Confirm</button>
                    </span>
                  ) : <span style={badge(false)}>{r.source === 'user' ? 'Set by a person' : 'Confirmed'}</span>}
                  <div style={sub}>{r.hits || 0} recognised</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function SaveReport({ result }: { result: ImportRuleSaveResult }) {
  const dropped = [...(result.dropped || []), ...((result.check?.dropped) || [])]
  const problems = result.check?.problems || []
  const conflicts = result.check?.conflicts || []
  const ok = !dropped.length && !problems.length && !conflicts.length
  return (
    <div style={{ ...(ok ? okBox : warnBox) }} role="status">
      {ok ? <><CheckCircle2 size={15} /> Saved. The keywords single this out among the stored documents.</> : (
        <div>
          <div style={{ fontWeight: 600 }}>Saved, with notes:</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {dropped.map((d, i) => (
              <li key={`d${i}`}>{describePattern(d.pattern)} — {d.reason}</li>
            ))}
            {problems.map((p, i) => <li key={`p${i}`}>{p}</li>)}
            {conflicts.map((c, i) => (
              <li key={`c${i}`}>The same document would also be recognised as “{c.label || c.doc_type}” — add a keyword only one of them has, or an exclusion.</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function describePattern(p: unknown): string {
  const x = (p || {}) as { kind?: string; value?: string }
  return x.value ? `${kindLabel(x.kind || '')} “${x.value}”` : 'A keyword'
}

function codeOf(label: string): string {
  const s = label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return (/^[a-z]/.test(s) ? s : s ? `t_${s}` : '').slice(0, 40)
}

const note: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #5e6472)', margin: 0 }
const sub: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }
const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', fontSize: 13, fontWeight: 600,
  color: 'var(--color-text-secondary, #5e6472)', borderBottom: '1px solid var(--color-border, #e6e5e0)',
}
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-border, #f0efea)', verticalAlign: 'top' }
const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 14, borderRadius: 6,
  border: '1px solid var(--color-border-strong, var(--color-border, #d5d7dc))',
  background: 'var(--color-input-bg, var(--color-bg, #fff))', color: 'var(--color-text, #15171c)',
}
const field: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 200px',
  color: 'var(--color-text-secondary, #5e6472)',
}
const btn: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', fontSize: 14,
  borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)', background: 'var(--color-bg, #fff)',
  color: 'inherit', cursor: 'pointer',
}
const smallBtn: React.CSSProperties = { ...btn, height: 26, padding: '0 8px', fontSize: 13 }
const primaryBtn: React.CSSProperties = {
  ...btn, background: 'var(--color-accent, #1d4ed8)', borderColor: 'var(--color-accent, #1d4ed8)', color: '#fff',
}
const iconBtn: React.CSSProperties = { ...btn, width: 32, padding: 0, justifyContent: 'center' }
const chip: React.CSSProperties = {
  fontSize: 13, padding: '1px 8px', borderRadius: 999, background: 'rgba(47,95,227,0.08)',
  color: 'var(--color-text, #15171c)', whiteSpace: 'nowrap',
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8,
  background: 'rgba(217,45,32,0.08)', color: '#b42318', fontSize: 14,
}
const warnBox: React.CSSProperties = {
  display: 'flex', gap: 6, padding: '8px 10px', borderRadius: 8, fontSize: 13,
  background: 'rgba(217,138,18,0.10)', color: '#7c4a03',
}
const okBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, fontSize: 13,
  background: 'rgba(22,101,52,0.08)', color: '#166534',
}
const suggestBox: React.CSSProperties = {
  padding: 10, borderRadius: 8, fontSize: 13, background: 'rgba(47,95,227,0.06)',
  border: '1px solid rgba(47,95,227,0.25)', color: 'var(--color-text, #15171c)',
}
function badge(warn: boolean): React.CSSProperties {
  return {
    fontSize: 13, padding: '1px 8px', borderRadius: 999,
    background: warn ? 'rgba(217,138,18,0.14)' : 'rgba(22,101,52,0.12)',
    color: warn ? '#a15c07' : '#166534',
  }
}
